import { LEVELS } from '@/src/game/config/levels'
import clsx from 'clsx'
import { motion } from 'motion/react'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'

type UpgradeBarProps = {
    barProgress: number
    unlockedCount: number
    maxUnlocks?: number
    currentCoins: number;
}

const UpgradeBar = function UpgradeBar({
    barProgress,
    unlockedCount,
    maxUnlocks = 4,
    currentCoins,
}: UpgradeBarProps) {

    const isStele = useSteleBreakpoint();
    const nextUpgradeItems = ['boots', 'gloves', 'helmet', 'vest'] as const

    return (
        <div
            className={clsx(
                'absolute flex justify-center items-center bg-white-gradient rounded-full',
                'p-0.5 w-[115%] h-4',
                'stele:p-3 stele:w-[110%] stele:h-16',
            )}
        >
            <div
                className={clsx(
                    'relative flex justify-center items-center bg-orange border-orange-stroke rounded-full w-full h-full ',
                    'px-0.5 py-px border-2',
                    'stele:px-2 stele:py-1 stele:border-8 ',
                )}
            >
                {/* Progress-Bar */}
                <div
                    className='top-0 left-0 absolute bg-orange-gradient rounded-full h-full transition-[width] duration-200 '
                    style={{ width: `${barProgress * 100}%` }}
                />

                <div
                    className={clsx(
                        'z-30 absolute duration-200 pointer-events-none ',
                        'w-7 h-7 -left-1 -translate-y-2.5',
                        'stele:w-28 stele:h-28 stele:-left-6 stele:-translate-y-10'
                    )}

                    aria-hidden
                >
                    <div className='relative flex justify-center items-center'>
                        <img
                            src='/images/CoinNumber.png'
                            className={clsx(
                                'w-12 h-12',
                                'min-w-12 min-h-12',
                                'stele:w-48 stele:h-48',
                                'stele:min-w-48 stele:min-h-48'
                            )}
                        />
                        <p className='absolute pr-1 font-grandstander font-black text-[15px] text-yellow-stroke stele:text-[60px] italic leading-0 stele:pr-4'>{currentCoins}</p>
                    </div>
                </div>

                {/* Bubbles */}
                <div
                    className={clsx(
                        'z-20 absolute flex justify-center items-center bg-shine-gradient rounded-full w-full h-full',
                        'gap-[17.5px] pl-12',
                        'stele:gap-0 stele:pl-56',
                    )}
                >
                    {Array.from({ length: maxUnlocks }).map((_, index) => {
                        const isUnlocked = index < unlockedCount
                        const levelRange = LEVELS[index]
                        const unlockTarget = levelRange
                            ? levelRange.end
                            : 0
                        return (
                            <div key={index}
                                className={clsx(
                                    'relative flex justify-center items-center w-13 stele:w-62 h-13 stele:h-62 stele:max-w-62 stele:max-h-62 stele:min-w-62 stele:min-h-62',
                                    isUnlocked && 'bg-black/35 rounded-full max-w-11 max-h-11 stele:max-w-52 stele:w-52 stele:h-52 stele:max-h-52 stele:min-w-52 stele:min-h-52'
                                )}
                            >
                                <motion.img
                                    src='/images/Bubble_Idle.png'
                                    alt=''
                                    aria-hidden
                                    className={clsx(
                                        'absolute w-full h-full object-cover ',
                                        isUnlocked && 'opacity-0'
                                    )}
                                    animate={{
                                        rotate: [20, -20],
                                        scaleX: [0.9, 1.06],
                                        scaleY: [1.06, 0.9],
                                    }}
                                    transition={{
                                        duration: 1.75,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                        repeatType: 'reverse',
                                    }}
                                />
                                <img
                                    src={`/images/editor/${nextUpgradeItems[index]}.png`}
                                    alt=''
                                    aria-hidden
                                    className={clsx(
                                        'absolute',
                                        'max-w-11 max-h-11',
                                        'stele:max-w-44 stele:max-h-44',
                                    )}
                                />
                                <span
                                    className={clsx(
                                        'absolute flex justify-center items-center font-bold text-white select-none',
                                        'w-10.75 h-10.75 -bottom-3.5 text-sm',
                                        'stele:w-43 stele:h-43 stele:text-[64px] stele:-bottom-14',
                                        isUnlocked && 'opacity-0'
                                    )}
                                    aria-hidden
                                >
                                    <p
                                        className={clsx(
                                            'absolute font-grandstander font-black text-yellow-stroke italic',
                                            'text-[7px] pr-0.5',
                                            'stele:text-[28px] stele:pr-2'
                                        )}>
                                        {LEVELS[index ?? 0].end}
                                    </p>
                                    <img
                                        src='/images/CoinNumber.png'
                                        className={clsx(
                                            'w-7 h-7',
                                            'min-w-7 min-h-7',
                                            'stele:w-28 stele:h-28',
                                            'stele:min-w-28 stele:min-h-28'
                                        )}
                                    />
                                </span>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default UpgradeBar
